import {
    Flex,
    Text,
    IconButton,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { AuthenticatedComponent } from '../../auth/keycloak';
import PageDataContext from '../../context/PageDataContext';
import { usePageDataCache } from '../../hooks/usePageDataCache';
import { getUserInventory, InventoryDef } from '../../service/inventory';
import { InformationIcon } from '../iconComponents/InformationIcon';
import { ASCENDING_ORDER, DESCENDING_ORDER, ToggleButtonGroup } from '../ToggleButtonGroup';
import { PendingListItem } from './PendingListItem';

const LogPrefix = 'PendingBalance';

const SORT_DATE = 'date';
const SORT_TYPE = 'type';
const SORT_AMOUNT = 'amount';

interface PendingBalanceProps {}

export const PendingBalance: AuthenticatedComponent<PendingBalanceProps> = ({ keycloak }) => {
    const { isLoadFromCache } = useContext(PageDataContext);

    const [displayPending, setDisplayPending] = useState<boolean>(false);
    const [pendingList, setPendingList] = usePageDataCache<InventoryDef[]>('pending_list', []);
    const [sortedList, setSortedList] = usePageDataCache<InventoryDef[]>('sorted_list', []);
    const [sortOrder, setSortOrder] = useState<string>(DESCENDING_ORDER);
    const [sortBy, setSortBy] = useState<string>(SORT_DATE);

    const getInventory = async () => {
        try {
            const invs = await getUserInventory(
                keycloak.subject,
                'streamsix_currency',
                '',
                keycloak.token,
            );

            let pendings: InventoryDef[] = [];
            invs.forEach((inv) => {
                if (inv.inventory_key.pending) {
                    pendings.push(inv);
                }
            });
            setPendingList(pendings);
        } catch (err: any) {
            console.error(err);
        }
    };

    const updateSort = (newSort: string, newOrder: string) => {
        setSortOrder(newOrder);
        setSortBy(newSort);
    };

    const sortData = () => {
        let pending = pendingList;
        switch (sortBy) {
            case SORT_DATE:
                pending = pendingList.sort((a, b) => {
                    if (a.inventory_key.pending && b.inventory_key.pending) {
                        return a.inventory_key.pending.release_date >
                            b.inventory_key.pending.release_date
                            ? 1
                            : -1;
                    } else {
                        return 0;
                    }
                });
                break;
            case SORT_TYPE:
                pending = pendingList.sort((a, b) => {
                    if (a.inventory_key.pending && b.inventory_key.pending) {
                        return a.inventory_key.pending?.source > b.inventory_key.pending?.source
                            ? 1
                            : -1;
                    } else {
                        return 0;
                    }
                });
                break;
            case SORT_AMOUNT:
                pending = pendingList.sort((a, b) => {
                    return a.quantity > b.quantity ? 1 : -1;
                });
                break;
        }
        if (sortOrder === ASCENDING_ORDER) {
            setSortedList(pending.reverse());
        } else {
            setSortedList(pending);
        }
    };

    useEffect(() => {
        sortData();
    }, [pendingList, sortBy, sortOrder]);

    useEffect(() => {
        if (!isLoadFromCache) {
            getInventory();
        }
    }, []);

    const renderContent = () => (
        <Flex className={'transaction-pending'}>
            <Flex className={'transaction-pending-info'}>
                <Text>Pending Balances:</Text>
                <Popover>
                    <PopoverTrigger>
                        <IconButton
                            variant={'unstyled'}
                            icon={<InformationIcon />}
                            aria-label={'balances-info'}
                        />
                    </PopoverTrigger>
                    <PopoverContent>
                        <PopoverHeader>Pending balance:</PopoverHeader>
                        <PopoverBody>
                            Pending Balances are not available for Withdrawal or Donation.
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
                <Flex className={'transaction-spacer'} />
                <Button
                    className={'view-button'}
                    onClick={() => {
                        setDisplayPending(!displayPending);
                    }}
                >
                    {displayPending ? 'COLLAPSE' : 'VIEW'}
                </Button>
            </Flex>
            {displayPending && (
                <Flex className="flex-column">
                    <>
                        {sortedList.length === 0 ? (
                            <Text>No Pending Balances</Text>
                        ) : (
                            <>
                                <Flex className={'pending-balance-filters'}>
                                    <ToggleButtonGroup
                                        options={[SORT_TYPE, SORT_DATE, SORT_AMOUNT]}
                                        onSortChange={updateSort}
                                    />
                                </Flex>

                                {sortedList.map((inv, idx) => {
                                    return (
                                        <PendingListItem
                                            key={idx}
                                            inventoryDef={inv}
                                            isOddRow={idx % 2 === 0}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </>
                </Flex>
            )}
        </Flex>
    );

    return renderContent();
};
